<template>
  <BSidebar
    :id="id"
    :visible="visible"
    class="bg-white"
    backdrop-variant="dark"
    backdrop
    right
    shadow
    width="560px"
    aria-hidden="false"
    @shown="onShown"
    @hidden="onClose"
  >
    <template #header>
      <div class="d-flex align-items-center w-100 p-[.75rem_1rem] gap-[1rem] border-bottom">
        <BButton
          size="sm"
          variant="primary"
          class="!p-[.6rem] rounded-lg d-flex align-items-center justify-content-center"
          @click="onClose"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.57 5.92999L3.5 12L9.57 18.07"
              stroke="white"
              stroke-width="2"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M20.5 12H3.67"
              stroke="white"
              stroke-width="2"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </BButton>

        <h5 class="text-[#333333] text-[20px] font-bold mb-0">
          {{ drawerTitle }}
        </h5>
      </div>
    </template>

    <BOverlay
      :show="loadingDetail"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <div
        class="p-[1.25rem_1.5rem] space-y-[1.5rem]"
        :class="{ 'invisible': loadingDetail }"
      >
        <!-- Status Banner -->
        <div
          class="py-[0.625rem] px-[1rem] text-center font-medium rounded-xl text-[14px]"
          :class="statusBadgeClass"
        >
          {{ statusBadgeLabel }}
        </div>

        <!-- Section 1: Detail Akun (Partner / Sebelumnya) -->
        <div>
          <h6 class="text-[#333333] font-medium text-[14px] mb-[0.625rem]">
            {{ isResolvedStatus || isReplacementAccount ? 'Detail Akun Sebelumnya' : 'Detail Akun Partner' }}
          </h6>
          <div class="border border-[#E2E2E2] rounded-xl p-[1rem] detail-info">
            <div
              v-if="isResolvedStatus"
              class="detail-row"
            >
              <span class="detail-label">Waktu Proses</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ processDateTime }}</span>
            </div>
            <div
              v-if="isResolvedStatus"
              class="detail-row"
            >
              <span class="detail-label">Admin Approver</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ detailData.admin_name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Waktu Kendala</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ issueDateTime }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Nama Akun</span>
              <span class="me-[.25rem]">:</span>
              <span
                class="detail-value overflow-hidden text-ellipsis whitespace-nowrap"
                :title="detailData.account_name"
              >{{ detailData.account_name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Jenis Akun</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ detailData.ad_account_type }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Email Partner</span>
              <span class="me-[.25rem]">:</span>
              <span
                class="detail-value overflow-hidden text-ellipsis whitespace-nowrap"
                :title="detailData.partner_email"
              >{{ detailData.partner_email }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">No Handphone</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value flex items-center gap-[6px]">
                <span>{{ detailData.partner_phone }}</span>
                <ButtonCopy
                  :text="detailData.partner_phone"
                  field-label="No Handphone"
                />
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">ID Ad Account</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value flex items-center gap-[6px]">
                <span>{{ detailData.ad_account_id }}</span>
                <ButtonCopy
                  :text="detailData.ad_account_id"
                  field-label="ID Ad Account"
                />
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Business Manager</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ detailData.business_manager }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Sumber Dana</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ detailData.funding_source }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Estimasi Saldo</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ IDR(detailData.estimated_balance || 0) }}</span>
            </div>
          </div>
        </div>

        <!-- Section 2: Detail Penggantian Akun (for replacement accounts) -->
        <div v-if="isReplacementAccount">
          <h6 class="text-[#333333] font-medium text-[14px] mb-[0.625rem]">
            Detail Penggantian Akun
          </h6>
          <div class="border border-[#E2E2E2] rounded-xl p-[1rem] detail-info">
            <!-- Waktu Proses -->
            <div class="detail-row">
              <span class="detail-label">Waktu Proses</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ processDateTime }}</span>
            </div>

            <!-- Admin Approver -->
            <div class="detail-row">
              <span class="detail-label">Admin Approver</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">{{ detailData.admin_name }}</span>
            </div>

            <!-- Nama Akun -->
            <div
              v-if="detailData.replacement && detailData.replacement.account_name"
              class="detail-row"
            >
              <span class="detail-label">Nama Akun</span>
              <span class="me-[.25rem]">:</span>
              <span
                class="detail-value overflow-hidden text-ellipsis whitespace-nowrap"
                :title="detailData.replacement.account_name "
              >
                {{ detailData.replacement.account_name }}
              </span>
            </div>

            <!-- Jenis Akun -->
            <div
              v-if="detailData.replacement && detailData.replacement.ad_account_type"
              class="detail-row"
            >
              <span class="detail-label">Jenis Akun</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">
                {{ detailData.replacement.ad_account_type }}
              </span>
            </div>

            <!-- ID Ad Account -->
            <div
              v-if="detailData.replacement && detailData.replacement.ad_account_id"
              class="detail-row"
            >
              <span class="detail-label">ID Ad Account</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value flex items-center gap-[6px]">
                <span>{{ detailData.replacement.ad_account_id }}</span>
                <ButtonCopy
                  :text="detailData.replacement.ad_account_id"
                  field-label="ID Ad Account"
                />
              </span>
            </div>

            <!-- Business Manager -->
            <div
              v-if="detailData.replacement && detailData.replacement.business_manager"
              class="detail-row"
            >
              <span class="detail-label">Business Manager</span>
              <span class="me-[.25rem]">:</span>
              <span class="detail-value">
                {{ detailData.replacement.business_manager }}
              </span>
            </div>
          </div>
        </div>

        <!-- Section 3: Catatan Kendala/Penggantian -->
        <div>
          <h6 class="text-[#333333] font-medium text-[14px] mb-[0.625rem]">
            {{ isReplacementAccount ? 'Catatan Penggantian' : 'Catatan Kendala' }}
          </h6>
          <div class="border border-[#E2E2E2] rounded-xl p-[1rem] min-h-[80px] text-[#626262] text-[13px] leading-relaxed">
            {{ isReplacementAccount ? (detailData.resolution_notes || '') : (detailData.notes || 'Tidak ada catatan detail kendala dari Meta') }}
          </div>
        </div>

        <!-- Action Buttons (for unresolved/active issues) -->
        <div
          v-if="!isResolvedStatus && !isReplacementAccount"
          class="flex items-center gap-[1rem] pt-[0.5rem]"
        >
          <BButton
            variant="outline-primary"
            class="w-full !rounded-lg !border-[#F95031] !text-[#F95031] hover:!bg-[#F95031] hover:!text-white font-medium py-[0.625rem]"
            @click="onClose"
          >
            Batal
          </BButton>
          <BButton
            :disabled="detailData.issue_type === 'on_review'"
            variant="primary"
            class="w-full !rounded-lg bg-[#F95031] text-white font-medium py-[0.625rem]"
            @click="handleResolve"
          >
            Selesaikan
          </BButton>
        </div>
      </div>
    </BOverlay>
  </BSidebar>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'
import { IDR } from '@/libs/currency'
import {
  DAY_MONTH_YEAR,
  TIME,
  DAY_MONTH_YEAR_COMMA_TIME,
} from '@/libs/filterDate'
import ButtonCopy from './ButtonCopy.vue'
import { ACCOUNT_STATUS_LABEL } from './constants'

export default {
  name: 'DrawerDetail',
  components: {
    ButtonCopy,
  },
  props: {
    id: {
      type: String,
      default: 'drawer-account-issue-detail',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    detail: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loadingDetail: true,
      detailData: {},
    }
  },
  computed: {
    isResolvedStatus() {
      return ['solved', 'closed'].includes(this.detailData.status)
    },
    drawerTitle() {
      if (this.isResolvedStatus || this.isReplacementAccount) {
        return 'Detail Kendala Terselesaikan'
      }
      return 'Detail Akun Terkendala'
    },
    isReplacementAccount() {
      return this.detailData.status === 'replacement' && Boolean(this.detailData.replacement)
    },
    issueDateTime() {
      if (this.detailData.created_at) {
        return `${DAY_MONTH_YEAR(this.detailData.created_at)} | ${TIME(this.detailData.created_at)}`
      }
      return '-'
    },
    processDateTime() {
      if (this.detailData.resolved_at) {
        return `${DAY_MONTH_YEAR(this.detailData.resolved_at)} | ${TIME(this.detailData.resolved_at)}`
      }
      return '-'
    },
    statusBadgeClass() {
      const classes = {
        payment_issue: 'bg-[#FFF2E2] text-[#FBA63C]',
        on_review: 'bg-[#E8F4FD] text-[#08A0F7]',
        restricted: 'bg-[#FFEDED] text-[#E31A1A]',
        replacement: 'bg-[#F3E8FF] text-[#8B5CF6]',
        solved: 'bg-[#DCF3EB] text-[#34A770]',
      }
      return classes[this.detailData.status] || classes[this.detailData.issue_type] || 'bg-gray-100 text-gray-700'
    },
    statusBadgeLabel() {
      return ACCOUNT_STATUS_LABEL[this.detailData.status] || ACCOUNT_STATUS_LABEL[this.detailData.issue_type] || ''
    },
  },
  methods: {
    IDR,
    DAY_MONTH_YEAR,
    TIME,
    DAY_MONTH_YEAR_COMMA_TIME,
    onClose() {
      this.$emit('on-close')
      setTimeout(() => {
        this.loadingDetail = true
      }, 800)
    },
    async onShown() {
      if (this.detail && this.detail.id) {
        await this.getAccountIssueDetail(this.detail.id)
      }
      this.loadingDetail = false
    },
    async getAccountIssueDetail(id) {
      const url = `komads/api/v1/admin/whitelist_account_issues/${id}/detail`

      try {
        const res = await newAxiosIns.get(url)
        const data = res && res.data ? res.data.data : null
        if (data) {
          this.detailData = { ...data }
        }
      } catch (error) {
        this.detailData = { ...this.detail }
      }
    },
    handleResolve() {
      this.$emit('resolve', this.detailData)
      this.onClose()
    },
  },
}
</script>

<style scoped>
.detail-row {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: flex-start;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  flex: 0 0 200px;
  color: #828282;
  font-size: 13px;
}

.detail-value {
  flex: 1;
  color: #333333;
  font-size: 13px;
  font-weight: 500;
}

::v-deep .b-sidebar-header,
::v-deep .b-sidebar-body {
  background-color: white;
}
</style>
